import {
  createContext,
  useCallback,
  useContext,
  type ReactNode,
} from "react";

import {
  AGENT_CATALOG_BACKGROUND_STALE_MS,
  type AgentRefreshMode,
} from "@/hooks/use-agents";

type AgentCatalogRefresh = (mode?: AgentRefreshMode) => Promise<unknown>;

const AgentCatalogRefreshContext = createContext<(() => void) | null>(null);

interface AgentCatalogRefreshProviderProps {
  refresh: AgentCatalogRefresh;
  updatedAt: number | null;
  children: ReactNode;
}

/**
 * Shares the shell-owned agent catalog revalidator with every picker. Keeping
 * the freshness check here means picker surfaces do not need to know about
 * the catalog hook or pass a callback through intermediate components.
 */
export function AgentCatalogRefreshProvider({
  refresh,
  updatedAt,
  children,
}: AgentCatalogRefreshProviderProps) {
  const refreshOnOpen = useCallback(() => {
    if (
      updatedAt !== null
      && Date.now() - updatedAt < AGENT_CATALOG_BACKGROUND_STALE_MS
    ) {
      return;
    }
    void refresh("background");
  }, [refresh, updatedAt]);

  return (
    <AgentCatalogRefreshContext.Provider value={refreshOnOpen}>
      {children}
    </AgentCatalogRefreshContext.Provider>
  );
}

/**
 * Is null outside the main shell so standalone/embedded pickers retain their
 * existing behavior without needing a catalog provider.
 */
export function useAgentCatalogRefreshOnOpen(): (() => void) | null {
  return useContext(AgentCatalogRefreshContext);
}
